import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Pencil, Trash2 } from 'lucide-react';
import { format, isToday, isYesterday, differenceInMinutes } from 'date-fns';
import { useData } from '@/contexts/DataContext';
import type { FeedEntry, SleepEntry, DiaperEntry } from '@/types';
import TimeWheelPicker from './TimeWheelPicker';

export type LogType = 'feed' | 'sleep' | 'diaper';

interface LogSheetProps {
  type: LogType;
  isOpen: boolean;
  onClose: () => void;
}

/* ─── Helpers ─────────────────────────────────────────── */
function dateLabel(date: Date): string {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'EEE, d MMM');
}
function fmtTime(date: Date) { return format(date, 'h:mm a'); }
function fmtDur(mins: number) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function groupByDate<T>(entries: T[], getDate: (e: T) => Date) {
  const map = new Map<string, { label: string; date: Date; entries: T[] }>();
  for (const e of entries) {
    const d = getDate(e);
    const key = format(d, 'yyyy-MM-dd');
    if (!map.has(key)) map.set(key, { label: dateLabel(d), date: d, entries: [] });
    map.get(key)!.entries.push(e);
  }
  return Array.from(map.values()).sort((a, b) => b.date.getTime() - a.date.getTime());
}

/* ─── Edit Forms ──────────────────────────────────────── */
const EditFeed = ({ entry, onSave, onClose }: { entry: FeedEntry; onSave: (u: Partial<FeedEntry>) => void; onClose: () => void }) => {
  const ts = new Date(entry.timestamp);
  const [feedType, setFeedType] = useState<'bottle' | 'breastfeed'>(entry.type === 'solids' ? 'bottle' : entry.type);
  const [amount, setAmount]     = useState(entry.amount ?? 60);
  const [duration, setDuration] = useState(entry.duration ?? 15);
  const [time, setTime]         = useState({ hour: ts.getHours(), minute: ts.getMinutes() });

  const handleSave = () => {
    const newTs = new Date(ts);
    newTs.setHours(time.hour, time.minute, 0, 0);
    onSave({
      timestamp: newTs,
      type: feedType,
      ...(feedType === 'bottle' ? { amount } : { duration }),
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {(['bottle', 'breastfeed'] as const).map((t) => (
          <button key={t} onClick={() => setFeedType(t)}
            className="flex-1 py-3 rounded-2xl text-sm font-semibold transition-all"
            style={feedType === t
              ? { background: 'hsl(var(--feed-bg))', color: 'hsl(var(--feed-accent))' }
              : { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
            {t === 'bottle' ? '🍼 Bottle' : '🤱 Breast'}
          </button>
        ))}
      </div>

      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2 text-center uppercase tracking-wide">Time</p>
        <TimeWheelPicker value={time} onChange={setTime} />
      </div>

      {feedType === 'bottle' ? (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-3 text-center uppercase tracking-wide">Amount (ml)</p>
          <div className="flex items-center justify-center gap-6">
            <button onClick={() => setAmount(Math.max(0, amount - 10))} className="w-11 h-11 rounded-full text-2xl font-bold flex items-center justify-center" style={{ background: 'hsl(var(--muted))' }}>−</button>
            <span className="text-4xl font-bold text-foreground w-24 text-center">{amount}</span>
            <button onClick={() => setAmount(amount + 10)} className="w-11 h-11 rounded-full text-2xl font-bold flex items-center justify-center" style={{ background: 'hsl(var(--muted))' }}>+</button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-xs font-semibold text-muted-foreground mb-3 text-center uppercase tracking-wide">Duration (min)</p>
          <div className="flex items-center justify-center gap-6">
            <button onClick={() => setDuration(Math.max(1, duration - 5))} className="w-11 h-11 rounded-full text-2xl font-bold flex items-center justify-center" style={{ background: 'hsl(var(--muted))' }}>−</button>
            <span className="text-4xl font-bold text-foreground w-24 text-center">{duration}</span>
            <button onClick={() => setDuration(duration + 5)} className="w-11 h-11 rounded-full text-2xl font-bold flex items-center justify-center" style={{ background: 'hsl(var(--muted))' }}>+</button>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="flex-1 py-3 rounded-2xl bg-muted font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Cancel</button>
        <button onClick={handleSave} className="flex-1 py-3 rounded-2xl text-white font-semibold" style={{ background: 'hsl(var(--primary))' }}>Save</button>
      </div>
    </div>
  );
};

const EditSleep = ({ entry, onSave, onClose }: { entry: SleepEntry; onSave: (u: Partial<SleepEntry>) => void; onClose: () => void }) => {
  const start = new Date(entry.startTime);
  const end   = new Date(entry.endTime);
  const [startTime, setStartTime] = useState({ hour: start.getHours(), minute: start.getMinutes() });
  const [endTime,   setEndTime]   = useState({ hour: end.getHours(),   minute: end.getMinutes() });

  const handleSave = () => {
    const newStart = new Date(start); newStart.setHours(startTime.hour, startTime.minute, 0, 0);
    const newEnd   = new Date(end);   newEnd.setHours(endTime.hour, endTime.minute, 0, 0);
    // If end <= start, assume end is next day
    if (newEnd <= newStart) newEnd.setDate(newEnd.getDate() + 1);
    onSave({ startTime: newStart, endTime: newEnd });
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2 text-center uppercase tracking-wide">Fell asleep at</p>
        <TimeWheelPicker value={startTime} onChange={setStartTime} />
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2 text-center uppercase tracking-wide">Woke up at</p>
        <TimeWheelPicker value={endTime} onChange={setEndTime} />
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="flex-1 py-3 rounded-2xl bg-muted font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Cancel</button>
        <button onClick={handleSave} className="flex-1 py-3 rounded-2xl text-white font-semibold" style={{ background: 'hsl(var(--sleep-accent))' }}>Save</button>
      </div>
    </div>
  );
};

const EditDiaper = ({ entry, onSave, onClose }: { entry: DiaperEntry; onSave: (u: Partial<DiaperEntry>) => void; onClose: () => void }) => {
  const ts = new Date(entry.timestamp);
  type DType = 'pee' | 'poop' | 'both';
  const initType: DType = entry.isPee && entry.isPoop ? 'both' : entry.isPoop ? 'poop' : 'pee';
  const [dType, setDType] = useState<DType>(initType);
  const [time, setTime]   = useState({ hour: ts.getHours(), minute: ts.getMinutes() });

  const handleSave = () => {
    const newTs = new Date(ts); newTs.setHours(time.hour, time.minute, 0, 0);
    onSave({ timestamp: newTs, isPee: dType !== 'poop', isPoop: dType !== 'pee' });
  };

  return (
    <div className="space-y-5">
      <div className="flex gap-2 justify-center">
        {([['pee','💧 Pee'], ['poop','💩 Poop'], ['both','Both']] as [DType, string][]).map(([v, label]) => (
          <button key={v} onClick={() => setDType(v)}
            className="flex-1 py-3 rounded-2xl text-sm font-semibold transition-all"
            style={dType === v
              ? { background: 'hsl(var(--diaper-bg))', color: 'hsl(var(--diaper-accent))' }
              : { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}>
            {label}
          </button>
        ))}
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-2 text-center uppercase tracking-wide">Time</p>
        <TimeWheelPicker value={time} onChange={setTime} />
      </div>
      <div className="flex gap-3 pt-1">
        <button onClick={onClose} className="flex-1 py-3 rounded-2xl bg-muted font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Cancel</button>
        <button onClick={handleSave} className="flex-1 py-3 rounded-2xl text-white font-semibold" style={{ background: 'hsl(var(--diaper-accent))' }}>Save</button>
      </div>
    </div>
  );
};

/* ─── Entry Rows ──────────────────────────────────────── */
const FeedRow = ({ entry, onEdit, onDelete, deleting, onDeleteConfirm, onDeleteCancel }: {
  entry: FeedEntry; onEdit: () => void;
  onDelete: () => void; deleting: boolean; onDeleteConfirm: () => void; onDeleteCancel: () => void;
}) => (
  <div
    className="bg-white rounded-2xl px-4 py-3 transition-all"
    style={deleting ? { border: '2px solid hsl(var(--destructive) / 0.3)', background: 'hsl(var(--destructive) / 0.05)' } : {}}
  >
    {deleting ? (
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium" style={{ color: 'hsl(var(--destructive))' }}>Delete this entry?</p>
        <div className="flex gap-2">
          <button onClick={onDeleteCancel} className="px-3 py-1.5 rounded-xl bg-muted text-muted-foreground text-xs font-semibold">Cancel</button>
          <button onClick={onDeleteConfirm} className="px-3 py-1.5 rounded-xl text-white text-xs font-semibold" style={{ background: 'hsl(var(--destructive))' }}>Delete</button>
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-3">
        <span className="text-2xl">{entry.type === 'bottle' ? '🍼' : '🤱'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {entry.type === 'bottle' ? `${entry.amount ?? 0} ml` : `${entry.duration ?? 0} min`}
          </p>
          <p className="text-xs text-muted-foreground">{entry.type === 'bottle' ? 'Bottle' : 'Breastfeed'} · {fmtTime(new Date(entry.timestamp))}</p>
        </div>
        <button onClick={onEdit} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
        <button onClick={onDelete} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
      </div>
    )}
  </div>
);

const SleepRow = ({ entry, onEdit, onDelete, deleting, onDeleteConfirm, onDeleteCancel }: {
  entry: SleepEntry; onEdit: () => void;
  onDelete: () => void; deleting: boolean; onDeleteConfirm: () => void; onDeleteCancel: () => void;
}) => {
  const dur = differenceInMinutes(new Date(entry.endTime), new Date(entry.startTime));
  return (
    <div
      className="bg-white rounded-2xl px-4 py-3 transition-all"
      style={deleting ? { border: '2px solid hsl(var(--destructive) / 0.3)', background: 'hsl(var(--destructive) / 0.05)' } : {}}
    >
      {deleting ? (
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium" style={{ color: 'hsl(var(--destructive))' }}>Delete this entry?</p>
          <div className="flex gap-2">
            <button onClick={onDeleteCancel} className="px-3 py-1.5 rounded-xl bg-muted text-muted-foreground text-xs font-semibold">Cancel</button>
            <button onClick={onDeleteConfirm} className="px-3 py-1.5 rounded-xl text-white text-xs font-semibold" style={{ background: 'hsl(var(--destructive))' }}>Delete</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-2xl">😴</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{fmtDur(Math.max(0, dur))}</p>
            <p className="text-xs text-muted-foreground">{fmtTime(new Date(entry.startTime))} → {fmtTime(new Date(entry.endTime))}</p>
          </div>
          <button onClick={onEdit} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
          <button onClick={onDelete} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
        </div>
      )}
    </div>
  );
};

const DiaperRow = ({ entry, onEdit, onDelete, deleting, onDeleteConfirm, onDeleteCancel }: {
  entry: DiaperEntry; onEdit: () => void;
  onDelete: () => void; deleting: boolean; onDeleteConfirm: () => void; onDeleteCancel: () => void;
}) => {
  const label = entry.isPee && entry.isPoop ? 'Pee + Poop' : entry.isPoop ? 'Poop' : 'Pee';
  const emoji = entry.isPee && entry.isPoop ? '💧💩' : entry.isPoop ? '💩' : '💧';
  return (
    <div
      className="bg-white rounded-2xl px-4 py-3 transition-all"
      style={deleting ? { border: '2px solid hsl(var(--destructive) / 0.3)', background: 'hsl(var(--destructive) / 0.05)' } : {}}
    >
      {deleting ? (
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium" style={{ color: 'hsl(var(--destructive))' }}>Delete this entry?</p>
          <div className="flex gap-2">
            <button onClick={onDeleteCancel} className="px-3 py-1.5 rounded-xl bg-muted text-muted-foreground text-xs font-semibold">Cancel</button>
            <button onClick={onDeleteConfirm} className="px-3 py-1.5 rounded-xl text-white text-xs font-semibold" style={{ background: 'hsl(var(--destructive))' }}>Delete</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-2xl">{emoji}</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">{label}</p>
            <p className="text-xs text-muted-foreground">{fmtTime(new Date(entry.timestamp))}</p>
          </div>
          <button onClick={onEdit} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-muted-foreground" /></button>
          <button onClick={onDelete} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center"><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></button>
        </div>
      )}
    </div>
  );
};

/* ─── Main LogSheet ───────────────────────────────────── */
const LOG_CONFIG = {
  feed:   { title: 'Feed Log',   emoji: '🍼', accentColor: 'hsl(var(--feed-accent))' },
  sleep:  { title: 'Sleep Log',  emoji: '😴', accentColor: 'hsl(var(--sleep-accent))' },
  diaper: { title: 'Diaper Log', emoji: '🧷', accentColor: 'hsl(var(--diaper-accent))' },
};

const LogSheet = ({ type, isOpen, onClose }: LogSheetProps) => {
  const navigate = useNavigate();
  const { data, deleteEntry, updateEntry } = useData();
  const cfg = LOG_CONFIG[type];

  const [editEntry, setEditEntry]   = useState<FeedEntry | SleepEntry | DiaperEntry | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-cancel delete confirmation after 4s
  useEffect(() => {
    if (deletingId) {
      if (deleteTimer.current) clearTimeout(deleteTimer.current);
      deleteTimer.current = setTimeout(() => setDeletingId(null), 4000);
    }
    return () => { if (deleteTimer.current) clearTimeout(deleteTimer.current); };
  }, [deletingId]);

  /* Sorted entries & groups */
  const { groups, todaySummary } = useMemo(() => {
    if (type === 'feed') {
      const sorted = [...data.feeds].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const groups = groupByDate(sorted, (e) => new Date(e.timestamp));
      const todayFeeds = data.feeds.filter((f) => isToday(new Date(f.timestamp)));
      const totalMl    = todayFeeds.filter((f) => f.type === 'bottle').reduce((s, f) => s + (f.amount ?? 0), 0);
      const breastCount = todayFeeds.filter((f) => f.type === 'breastfeed').length;
      const parts = [];
      if (totalMl > 0)    parts.push(`${totalMl} ml bottle`);
      if (breastCount > 0) parts.push(`${breastCount} breastfeed${breastCount > 1 ? 's' : ''}`);
      return { groups, todaySummary: parts.join(' · ') || 'No feeds today' };
    }
    if (type === 'sleep') {
      const sorted = [...data.sleeps].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
      const groups = groupByDate(sorted, (e) => new Date(e.startTime));
      const todayMins = data.sleeps
        .filter((s) => isToday(new Date(s.startTime)) || isToday(new Date(s.endTime)))
        .reduce((sum, s) => sum + differenceInMinutes(new Date(s.endTime), new Date(s.startTime)), 0);
      return { groups, todaySummary: todayMins > 0 ? `${fmtDur(todayMins)} today` : 'No sleep logged today' };
    }
    // diaper
    const sorted = [...data.diapers].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    const groups = groupByDate(sorted, (e) => new Date(e.timestamp));
    const todayCount = data.diapers.filter((d) => isToday(new Date(d.timestamp))).length;
    return { groups, todaySummary: todayCount > 0 ? `${todayCount} change${todayCount > 1 ? 's' : ''} today` : 'No changes today' };
  }, [data, type]);

  if (!isOpen) return null;

  const handleDelete = async (id: string) => {
    const dataType = type === 'feed' ? 'feeds' : type === 'sleep' ? 'sleeps' : 'diapers';
    await deleteEntry(dataType, id);
    setDeletingId(null);
  };

  const handleSaveEdit = async (id: string, updates: any) => {
    const dataType = type === 'feed' ? 'feeds' : type === 'sleep' ? 'sleeps' : 'diapers';
    await updateEntry(dataType, id, updates);
    setEditEntry(null);
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Main Sheet */}
      <div className="relative bg-[hsl(250,60%,97%)] rounded-t-3xl max-h-[90vh] flex flex-col"
           style={{ animation: 'slideUp 0.3s ease-out' }}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <div>
            <h2 className="text-lg font-bold text-foreground">{cfg.emoji} {cfg.title}</h2>
            <p className="text-xs font-medium mt-0.5" style={{ color: cfg.accentColor }}>{todaySummary}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onClose(); navigate(`/add-${type}`); }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-white text-sm font-semibold active:scale-95"
            >
              + Add
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white flex items-center justify-center">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Entry List */}
        <div className="overflow-y-auto flex-1 px-4 pb-8">
          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-5xl mb-3">{cfg.emoji}</span>
              <p className="text-muted-foreground text-sm">No entries yet.</p>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.label} className="mb-5">
                {/* Sticky date label */}
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2 py-1">{group.label}</p>
                <div className="space-y-2">
                  {type === 'feed' && (group.entries as FeedEntry[]).map((entry) => (
                    <FeedRow
                      key={entry.id} entry={entry}
                      onEdit={() => setEditEntry(entry)}
                      onDelete={() => setDeletingId(entry.id)}
                      deleting={deletingId === entry.id}
                      onDeleteConfirm={() => handleDelete(entry.id)}
                      onDeleteCancel={() => setDeletingId(null)}
                    />
                  ))}
                  {type === 'sleep' && (group.entries as SleepEntry[]).map((entry) => (
                    <SleepRow
                      key={entry.id} entry={entry}
                      onEdit={() => setEditEntry(entry)}
                      onDelete={() => setDeletingId(entry.id)}
                      deleting={deletingId === entry.id}
                      onDeleteConfirm={() => handleDelete(entry.id)}
                      onDeleteCancel={() => setDeletingId(null)}
                    />
                  ))}
                  {type === 'diaper' && (group.entries as DiaperEntry[]).map((entry) => (
                    <DiaperRow
                      key={entry.id} entry={entry}
                      onEdit={() => setEditEntry(entry)}
                      onDelete={() => setDeletingId(entry.id)}
                      deleting={deletingId === entry.id}
                      onDeleteConfirm={() => handleDelete(entry.id)}
                      onDeleteCancel={() => setDeletingId(null)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Sub-Sheet */}
      {editEntry && (
        <div className="absolute inset-0 z-10 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setEditEntry(null)} />
          <div className="relative bg-white rounded-t-3xl px-5 pt-5 pb-10"
               style={{ animation: 'slideUp 0.25s ease-out' }}>
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 rounded-full bg-muted" />
            </div>
            <h3 className="text-base font-bold text-foreground mb-5">Edit Entry</h3>
            {type === 'feed' && (
              <EditFeed
                entry={editEntry as FeedEntry}
                onSave={(u) => handleSaveEdit(editEntry.id, u)}
                onClose={() => setEditEntry(null)}
              />
            )}
            {type === 'sleep' && (
              <EditSleep
                entry={editEntry as SleepEntry}
                onSave={(u) => handleSaveEdit(editEntry.id, u)}
                onClose={() => setEditEntry(null)}
              />
            )}
            {type === 'diaper' && (
              <EditDiaper
                entry={editEntry as DiaperEntry}
                onSave={(u) => handleSaveEdit(editEntry.id, u)}
                onClose={() => setEditEntry(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LogSheet;
